// Display helpers for multi-quantity modifiers ("2x Chocolate").
//
// Shared contract, Rule 3: serialized order item options carry a computed
// `unitquantity` (int). Older API builds don't send it, so every reader
// derives it from the legacy total-units `quantity` field:
//   unitquantity = itemQty > 0 && quantity % itemQty == 0
//     ? quantity / itemQty
//     : quantity
// Legacy rows have quantity == lineQty, so they derive to 1 -> no prefix ->
// today's output, bit for bit.

export const getUnitQuantity = (option, itemQuantity) => {
  const unit = Number(option?.unitquantity);
  if (Number.isFinite(unit) && unit > 0) return unit;

  const total = Number(option?.quantity);
  if (!Number.isFinite(total) || total <= 0) return 1;

  const itemQty = Number(itemQuantity);
  if (Number.isFinite(itemQty) && itemQty > 0 && total % itemQty === 0) {
    return total / itemQty;
  }
  return total;
};

// "2x Chocolate" when the unit quantity is above 1; today's bare name at 1.
// ASCII "x", no space between the number and the x (deliberate: kitchen_v2's
// receipt indent logic keys on ' x ' for item lines).
export const formatOptionName = (option, itemQuantity) => {
  const unit = getUnitQuantity(option, itemQuantity);
  const name = option?.name ?? '';
  return unit > 1 ? `${unit}x ${name}` : name;
};

// Price annotations show EXTENDED amounts (unit price x unit qty) so the
// listed lines sum to the totals.
export const getExtendedOptionPrice = (option, itemQuantity) =>
  Number(option?.price || 0) * getUnitQuantity(option, itemQuantity);

// Total units of this option across the whole order line — the wire-contract
// meaning of `quantity` (unitQty x lineQty). Used by the client-side
// line-total recomputations. Rows that predate the quantity column fall back
// to the item quantity (unit qty 1), which reproduces today's
// `price x item.quantity` math exactly.
export const getTotalOptionUnits = (option, itemQuantity) => {
  const total = Number(option?.quantity);
  if (Number.isFinite(total) && total > 0) return total;
  const itemQty = Number(itemQuantity);
  return Number.isFinite(itemQty) && itemQty > 0 ? itemQty : 1;
};
